import {
  ActivityItem,
  Link,
  mergeStyleSets,
  Separator,
  Stack
} from '@fluentui/react';
import { LockSolidIcon } from '@fluentui/react-icons-mdl2';
import {
  Box,
  Flex,
  Label,
  Skeleton,
} from '@fluentui/react-northstar';
import { FontIcon } from '@fluentui/react/lib/Icon';
import i18next from 'i18next';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import AddUserIcon from '../../../../svg/add-user.svg';
import AdditionalInfoIcon from '../../../../svg/additional-info-icon.svg';
import AcceptIcon, { default as ApprovalAcceptIcon } from '../../../../svg/approval-accepted.svg';
import AddIcon, { default as ApprovalAddIcon } from '../../../../svg/approval-added.svg';
import DeclinedIcon, { default as ApprovalDeclinedIcon } from '../../../../svg/approval-declined.svg';
import RemoveFileAttachmentsIcon from '../../../../svg/attach-delete.svg';
import LifecyclePhaseIcon from '../../../../svg/lifecycle-phase-icon.svg';
import LifecycleIcon from '../../../../svg/lifecycle.svg';
import MergeIcon from '../../../../svg/merge-activity.svg';
import RelateIcon from '../../../../svg/relate-activity.svg';
import RemoveUserIcon from '../../../../svg/remove-user.svg';
import TimeEntryAddedIcon from '../../../../svg/time-entry-added.svg';
import TimeEntryChangedIcon from '../../../../svg/time-entry-changed.svg';
import TimeEntryRemovedIcon from '../../../../svg/time-entry-removed.svg';
import UnRelateIcon from '../../../../svg/unrelate-activity.svg';
import { appState, AppStateValue, IRequestPageConfig } from "../../../AppState";
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import { formatTimeInput } from '../../../templates/CustomComponents/Utils/CustomElementUtils';
import { getCachedUsers } from '../../cache/PlatformUserCache';
import { CommonHelper } from '../../common/CommonHelper';
import { ConvertDateToLocale } from '../../common/ConvertDate';
import '../../common/StringExtensions';
import { Comment } from '../../interfaces/ticket.interface';
import { Guid } from '../../services/guid.service';
import { platformService } from '../../services/platform.service';
import { ACTIVITY_ACTION, ACTIVITY_FIELD, ACTIVITY_TASK_FILED, markdownComponents } from '../../utils/constants';
import { replaceTokenWithComponent } from '../../utils/helper';
import { registerThumbnailSource } from '../EditTicketAttachments/NewAttachmentFlow/thumbnailResolver';
import { PlatformUserAvatar } from '../PlatformUserAvatar';
import RelativeDate from '../RelativeDate';
import { renderTimeInputInRequest } from '../TicketTemplateAnswer';
import { DurationText } from '../TicketTimeTracking/DurationText';
import { parseTimeEntryActivity } from '../TicketTimeTracking/durationFormatter';
import { entryDatePart, entryTimePart } from '../TicketTimeTracking/entryDate';
import { UserAvatar } from '../UserAvatar';
import { EditTicketAttachmentsProps } from './EditableTicketChat/EditableTicketAttachment';
import {
  collectTikitImgTagStrings,
  extractTikitImgItemId,
  getAttachmentPublicUrl,
  renderAttachments,
} from './EditableTicketChat/renderAttachments';
import TicketChatContent from './TicketChatContent';
import { ActivityItemProps, ActivityStateProps, AttachmentProps, AUCProps, TagProps } from './TicketConversation.interfaces';
import { UserProps } from './TicketConversation.types';
import UserInfoLink from './components/UserInfoLink';
import { renderCustomFormAnswer } from './customFormAnswerDisplay';

const classNames = mergeStyleSets({
  activityItem: {
    marginTop: 0,
    padding: 0,
    marginLeft: 7,
    marginRight: -10
  },
  userLink: {
    paddingTop: '10px',
    color: 'var(--mgt-theme-brand-foreground1)',
    ':hover, :active, :focus': {
      color: 'var(--mgt-theme-brand-foreground1) !important'
    }
  },
  icon: {
    fontSize: 18,
    background: 'var(--mgt-theme-background2)',
    paddingBottom: '10px',
    paddingTop: '5px',
    color: '#8F90A6'
  },
  // Sized to sit with the other activity SVGs, which are 19-21px tall. The badge is part
  // of the artwork rather than a positioned element, so there is nothing to align here.
  timeEntryIcon: {
    width: 21,
    height: 21,
    marginTop: '2px'
  },
  containerDiv: {
    paddingLeft: 15,
    position: 'relative',
    overflowY: 'scroll',
    maxHeight: 'calc(100vh - 200px) !important',
    zIndex: 2000
  },
  divider: {
    width: '9px',
    top: '25px',
    left: '23px',
    position: 'absolute',
    height: '100vh !important',
    ...{ '::before': { background: 'gray !important' } }
  },
  paddingTop10: {
    paddingTop: '10px'
  },
  lightBold: {
    fontWeight: 600
  },
  overlapLine: {
    position: 'absolute',
    left: 0,
    top: -10,
    height: '100vh',
    background: 'var(--mgt-theme-background2)',
    width: '100%',
    zIndex: 3000
  },
  automations: {
    paddingTop: '10px !important',
    backgroundColor: 'transparent !important',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    fontWeight: 600,
    fontSize: 'unset !important',
    color: 'var(--mgt-theme-default-foreground1) !important',
    height: 'auto !important'
  },
  commentDefault: {
    maxWidth: '100%',
    minHeiht: '16px',
    display: 'flex',
    borderRadius: '4px',
    margin: '5px 0 5px 0',
    flex: 'auto',
    overflow: 'hidden'
  },
  commentExpand: {
    maxWidth: '100%',
    height: '100%',
    display: 'flex',
    borderRadius: '4px',
    margin: '5px 0 5px 0',
    flex: 'auto'
  },
  toggleFlex: {
    position: 'absolute !important',
    right: -20,
    top: 25
  },
  syncButton: {
    minWidth: '0 !important',
    padding: '0 !important'
  },

  moreActivity: {
    marginTop: 20,
    paddingBlock: 10
  },
  activityAction: {
    color: 'var(--mgt-theme-default-foreground2)',
    fontWeight: 600
  },
  activityDefault: {
    color: 'var(--mgt-theme-default-foreground)'
  },
  hint: {
    color: 'var(--mgt-theme-default-foreground2)',
    fontWeight: 500
  },
  commentWithAttachments: {
    background: 'var(--mgt-theme-brand-background1)',
    '.ui-chat__message': {
      background: 'var(--mgt-theme-brand-background1)',
      width: '100%',
      maxWidth: '100%',
      margin: 0
    }
  },
  lifecycleIcons: {
    marginLeft: '19px',
    'svg': {
      marginTop: '16px'
    },
    'i': {
      marginTop: '0px',
    }
  },
  resolutionNoteContents: {
    fontSize: 'larger !important',
    color: 'var(--mgt-theme-default-foreground)'
  },
  resolutionNoteContainer: {
    backgroundColor: 'var(--mgt-theme-brand-background1)',
    padding: '10px',
    '.ms-Icon': { backgroundColor: 'var(--mgt-theme-brand-background1)' }
  },
  internalResolutionNoteContainer: {
    borderRadius: '4px'
  }
});

const CONFIG = {
  QUERY_SKIP: 0,
  QUERY_TOP: 40,
  INCREMENT_SKIP: 40
};

const Constants = {
  Automations: 'Automations',
  TicketLifecycle: "Lifecycle"
};


const defaultActivityState: ActivityStateProps = {
  QuerySkip: CONFIG.QUERY_SKIP,
  Loading: true,
  Activities: []
};

const api = new platformService();

export const getTicketActivities = async (
  ticketId: number,
  top: number,
  skip: number,
  commentOnly: boolean,
  filter: string
): Promise<ActivityItemProps[]> => {
  try {
    const ticketActivities = await api.getTicketActivities(
      ticketId,
      top,
      skip,
      commentOnly,
      filter
    );
    return Promise.resolve(ticketActivities.data.value);
  } catch (err) {
    return Promise.reject(err);
  }
};

// Utilities

const userInfo = (value: string): UserProps => {
  return CommonHelper.toObject<UserProps>(value);
};

const getRelationIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.MERGED:
      return <MergeIcon />;
    case ACTIVITY_ACTION.RELATED:
      return <RelateIcon />;
    case ACTIVITY_ACTION.UNRELATED:
      return <UnRelateIcon />;
    default:
      return <></>;
  }
}

const getTicketCollaboratorsIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.ADDED:
      return <AddUserIcon />;
    case ACTIVITY_ACTION.REMOVED:
      return <RemoveUserIcon />;
    default:
      return <></>;
  }
}

const getApprovalsIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.ADDED:
      return <AddUserIcon />;
    case ACTIVITY_ACTION.APPROVED:
      return <ApprovalAcceptIcon />;
    case ACTIVITY_ACTION.DECLINED:
      return <ApprovalDeclinedIcon />;
    case ACTIVITY_ACTION.CHANGED:
      return <AcceptIcon />;
    default:
      return <></>;
  }
}
/**
 * Clock with an action badge, so logged time is distinguishable at a glance from tags,
 * attachments and everything else sharing the generic added/removed/changed icons.
 *
 * Drawn as SVG in the same family as the approval icons rather than composed from a font
 * glyph plus a CSS circle. The badge is cut into the clock by a mask and its symbol is
 * knocked out of the fill, which is what makes it crisp at 18px - a CSS badge over a
 * FontIcon renders in a different font at a different scale and reads soft.
 */
const getTimeEntryIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.ADDED:
      return <TimeEntryAddedIcon className={classNames.timeEntryIcon} />;
    case ACTIVITY_ACTION.DELETED:
    case ACTIVITY_ACTION.REMOVED:
      return <TimeEntryRemovedIcon className={classNames.timeEntryIcon} />;
    case ACTIVITY_ACTION.UPDATED:
    case ACTIVITY_ACTION.CHANGED:
      return <TimeEntryChangedIcon className={classNames.timeEntryIcon} />;
    default:
      return <TimeEntryAddedIcon className={classNames.timeEntryIcon} />;
  }
};

const getTicketTaskIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.CREATED:
    case ACTIVITY_ACTION.ADDED:
      return <AddIcon />;
    case ACTIVITY_ACTION.CHANGED:
      return <AcceptIcon />;
    case ACTIVITY_ACTION.REMOVED:
      return <DeclinedIcon />;
    default:
      return <></>;
  }
}

const getFileAttachmentIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.ATTACHED:
      return <FontIcon className={classNames.icon} iconName='Attach' />
    case ACTIVITY_ACTION.REMOVED:
      return <RemoveFileAttachmentsIcon height='35' />
    default:
      break;
  }
}

const ticketLifecyclePhaseChange = (phase: TicketLifecyclePhase) => {
  return <>
    <span>
      <b>{phase.Name}</b> {(phase.IsManuallyTransitionedTo) && (<span> from phase <b>{phase.PreviousPhaseName}</b></span>)}
      {(phase.TransitionMessage?.length ?? 0) > 0 && (
        <Flex vAlign="center" gap="gap.smaller">
          <span><AdditionalInfoIcon /></span>{' '}
          <span className={classNames.hint}>{phase.TransitionMessage}</span>
        </Flex>
      )}
    </span>
  </>
}

const ticketLifecycleStatusChange = (lifecycle: TicketLifecycle) => (<span><b>{lifecycle.Status.Value}</b></span>);

const ticketTaskChange = (task: any, activity: ActivityItemProps, isEndUserPage: boolean) => {
  if (task.hasOwnProperty('Field')) {
    switch (task.Field) {
      case ACTIVITY_TASK_FILED.TITLE:
        return <>
          <span>
            Task {task.Field.normalize()} from <b>{task.OldTitle.toLocaleLowerCase()}</b> to{' '}<b>{task.Title.toLocaleLowerCase()}</b>
          </span>
        </>
      case ACTIVITY_TASK_FILED.STATUS_ID:
        const taskDataPhase: TicketLifecyclePhase = (task[ACTIVITY_TASK_FILED.LIFECYCLE_PHASE]?.length ?? 0) > 0 ? JSON.parse(task[ACTIVITY_TASK_FILED.LIFECYCLE_PHASE]) : null;

        let modifierInfo = userInfo(activity.ModifierInfo);
        if (modifierInfo.Name == Constants.TicketLifecycle)
          return <>
            <span>
              {' '} for task <b>{task.Title}</b>
            </span>
            {(taskDataPhase?.TransitionMessage ?? "").length > 0 && (
              <Flex vAlign="center" gap="gap.smaller">
                <AdditionalInfoIcon />{' '}
                <span className={classNames.hint}>{taskDataPhase.TransitionMessage}</span>
              </Flex>
            )}
          </>
        else
          return <>
            <span>
              {' '}<b>{task.Title}</b> {"Status".normalize()} <b>{task.Status.toLocaleLowerCase()}</b>
            </span>
          </>
      case ACTIVITY_TASK_FILED.ASSIGNEE_ID:
        return <>
          <span>
            {' '}<b>{task.Title}</b> {"Assignee".normalize()} <b>{task.Assignee?.toLocaleLowerCase()}</b>
          </span>
        </>
      default:
        return <></>
    }
  } else {
    switch (task.property) {
      case ACTIVITY_TASK_FILED.TITLE:
        return <Trans i18nKey='ticket-details.ticket-conversation.tasks.title' values={{ oldTitle: task.originalValue || "", newTitle: task.value }} />;
      case ACTIVITY_TASK_FILED.ASSIGNEE_ID:
        return <><Trans i18nKey='ticket-details.ticket-conversation.tasks.assignee' values={{ title: task.title || "" }} /><UserInfoLink user={{ Id: task.value } as UserProps} isEndUserPage={isEndUserPage} /></>;
      case ACTIVITY_TASK_FILED.STATUS_ID:
        let modifierInfo = userInfo(activity.ModifierInfo);
        if (modifierInfo.Name == Constants.TicketLifecycle)
          return <>
            <Trans i18nKey='ticket-details.ticket-conversation.tasks.phase' values={{ title: task.title || "" }} />
            {(task.transitionMessage?.length ?? 0) > 0 && (<Flex vAlign="center" gap="gap.smaller"><AdditionalInfoIcon /> <span className={classNames.hint}>{task.transitionMessage}</span></Flex>)}
          </>;

        return <Trans i18nKey='ticket-details.ticket-conversation.tasks.status' values={{ title: task.title || "", status: task.value }} />;
      default:
        let propertyString = i18next.t(`ticket-details.ticket-conversation.field.${task.property.toLocaleLowerCase()}`);
        return <Trans i18nKey='ticket-details.ticket-conversation.tasks.field' values={{ title: task.title || "", property: propertyString, value: task.value }} />;
    }
  }
}

const ticketApprovalChange = (approval: any, activity: ActivityItemProps, isEndUserPage: boolean) => {
  switch (approval.property) {
    case ACTIVITY_TASK_FILED.TITLE:
      return <Trans i18nKey='ticket-details.ticket-conversation.approvals.title' values={{ oldTitle: approval.originalValue || "", newTitle: approval.value }} />;
    default:
      let propertyString = i18next.t(`ticket-details.ticket-conversation.field.${approval.property.toLocaleLowerCase()}`);
      return <Trans i18nKey='ticket-details.ticket-conversation.approvals.field' values={{ title: approval.title || "", property: propertyString, value: approval.value }} />;
  }
}

const iconMapping = (activity: ActivityItemProps) => {
  if (userInfo(activity.ModifierInfo).Name === Constants.Automations)
    return 'Robot';

  switch (activity.Action) {
    case ACTIVITY_ACTION.COMMENTED:
      return 'CommentAdd';
    case ACTIVITY_ACTION.REMOVED:
      return 'RemoveFilter';
    case ACTIVITY_ACTION.FILLED:
    case ACTIVITY_ACTION.ADDED:
      return 'CircleAddition';
    case ACTIVITY_ACTION.CHANGED:
    case ACTIVITY_FIELD.CUSTOM_PROPERTIES:
      return 'FieldChanged';
    case ACTIVITY_ACTION.CREATED:
      return 'Ticket';
    case ACTIVITY_ACTION.RENAMED:
      return 'Rename';
    case ACTIVITY_ACTION.ATTACHED:
      return 'Attach';
    case ACTIVITY_ACTION.ASSIGNED:
      return 'FollowUser';
    default:
      return 'StatusCircleBlock';
  }
};
/**
 * Time entries store a JSON payload rather than a display string, so that the duration
 * can be formatted in the reader's language instead of the writer's. Without this the
 * feed prints the raw object.
 *
 * The sentence always carries all three facts - duration, date and start time - and puts an
 * arrow only on the ones that moved. An edit that changed the hours still says which day it
 * was for; one that moved the entry to another day says so without repeating the duration as
 * though it had changed too.
 *
 * Dates are read the way the tab reads them, from the row's own flag and offset: a date-only
 * row pinned to UTC, a timed row shifted onto the clock it was written on. The two surfaces
 * have to agree, and they sit one tab apart.
 *
 * It also names the owner when that is not the person who made the change. An Admin may edit
 * or delete another agent's time, and the row already leads with who acted - so without this
 * "Adele Vance deleted time spent 15m" reads as Adele's own time whoever logged it (35263).
 */
const timeEntryDescription = (activity: ActivityItemProps, t: any, isEndUserPage: boolean) => {
  const parsed = parseTimeEntryActivity(activity.Value);
  if (!parsed) return <span>{t('ticket-details.ticket-conversation.field.time-entries')}</span>;

  // Only when the two are actually different people. Logging your own time is the overwhelming
  // majority of rows, and "Miriam deleted Miriam's time spent 15m" is worse than the sentence
  // it replaces. Null - every row written before the payload carried an owner - reads as "say
  // nothing" for the same reason: guessing would name the modifier, which is the bug.
  const ownedByOther = parsed.userId !== null && parsed.userId !== activity.ModifiedById;

  // The whole clause is one translatable key rather than a name glued to a possessive suffix,
  // so a translator can put the placeholder where their language needs it - "Zeitaufwand von
  // <owner>" rather than an "'s" that has no equivalent to attach to.
  const label = ownedByOther
    ? (
      <Trans
        i18nKey="ticket-details.ticket-conversation.field.time-entries-other"
        components={{
          owner: <UserInfoLink user={{ Id: parsed.userId } as UserProps} isEndUserPage={isEndUserPage} />
        }}
      />
    )
    : t('ticket-details.ticket-conversation.field.time-entries');

  // Each date is read with its OWN flag. They can differ on one save: an entry logged before
  // the tenant captured start times gains the flag when it is edited, so its previous value is
  // a pinned day while its new one is a real instant. Reading both as instants is what made an
  // edit that changed only the duration report "8/29/2026 -> 8/30/2026 - 5:00 PM -> 6:31 PM" at
  // UTC-7 - midnight UTC read as a moment lands the previous evening.
  //
  // Falls back to the current flag on a payload written before the server sent the previous
  // one, which is exactly how those rows have always rendered.
  const hasTime = parsed.hasTimeOfDay ?? false;
  const hadTime = parsed.originalHasTimeOfDay ?? hasTime;

  const datePart = (iso: string, flag: boolean) => entryDatePart(iso, flag);
  const timePart = (iso: string, flag: boolean) => entryTimePart(iso, flag);

  const durationMoved = parsed.originalMinutes !== null && parsed.originalMinutes !== parsed.minutes;

  // Each half is compared on its own, so an edit that only moved the clock does not print the
  // same date either side of an arrow. Compared as rendered rather than as raw values: two
  // instants a second apart are the same entry to a reader, and an arrow between identical
  // text reads as a fault.
  const comparable = parsed.entryDate !== null && parsed.originalEntryDate !== null;

  const currentDate = parsed.entryDate !== null ? datePart(parsed.entryDate, hasTime) : null;
  const previousDate = comparable ? datePart(parsed.originalEntryDate, hadTime) : null;
  const dateMoved = previousDate !== null && previousDate !== currentDate;

  const currentTime = parsed.entryDate !== null ? timePart(parsed.entryDate, hasTime) : null;
  const previousTime = comparable ? timePart(parsed.originalEntryDate, hadTime) : null;

  // A row that GAINED a time has no previous one, so there is nothing for the arrow to point
  // from - it reads as the new time alone, the same as a fresh entry. Only a time that actually
  // changed gets an arrow.
  const timeMoved = previousTime !== null && currentTime !== null && previousTime !== currentTime;

  // Reads left to right as duration, date, time - always all three that the row has, with an
  // arrow only on the ones that moved.
  const pair = (before: string, after: string, moved: boolean) =>
    moved ? (<><b>{before}</b>{' → '}<b>{after}</b></>) : <b>{after}</b>;

  return (
    <span>
      {label}
      <b>
        {durationMoved ? (
          <>
            <DurationText minutes={parsed.originalMinutes} />
            {' → '}
            <DurationText minutes={parsed.minutes} />
          </>
        ) : (
          <DurationText minutes={parsed.minutes} />
        )}
      </b>
      {/* Absent on entries written before the payload carried a date - those rows keep the
          sentence they have always had rather than gaining an empty clause. */}
      {currentDate !== null && (
        <>
          {' · '}
          {pair(previousDate ?? currentDate, currentDate, dateMoved)}
        </>
      )}
      {/* Only when the row recorded one - a date-only entry has no time half to report. */}
      {currentTime !== null && (
        <>
          {' · '}
          {pair(previousTime ?? currentTime, currentTime, timeMoved)}
        </>
      )}
    </span>
  );
};

const descriptionMapping = (activity: ActivityItemProps, isEndUserPage: boolean, t: any, currentState: AppStateValue) => {
  if (activity.Field === ACTIVITY_FIELD.TIME_ENTRIES) return timeEntryDescription(activity, t, isEndUserPage);

  const _add_remove = (<span>{t(`ticket-details.ticket-conversation.field.${activity.Field.hyphenize()}`)}<b><PropertyResolver activity={activity} isEndUserPage={isEndUserPage} /></b></span>);
  const _relate_unrelate = (<span>{t("ticket-details.ticket-conversation.ticket")}: <b>{activity.RelatedTicketIds}</b></span>);
  const _approval_response = (<span>{<b>{activity.Value}</b>}</span>);
  switch (activity.Action) {
    case ACTIVITY_ACTION.CHANGED:
    case ACTIVITY_ACTION.TRANSITIONED:
      return <ChangeResolver activity={activity} isEndUserPage={isEndUserPage} />
    case ACTIVITY_ACTION.ADDED:
      return _add_remove;
    case ACTIVITY_ACTION.CREATED:
      return (activity.Field === ACTIVITY_FIELD.TICKET_TASKS) ? (<span><b>{activity.Value}</b></span>) : (<span>{t('ticket-details.ticket-conversation.created')}<b>{activity.TicketId}</b></span>);
    case ACTIVITY_ACTION.RENAMED:
      return (<span>{t(`ticket-details.ticket-conversation.field.${activity.Field.hyphenize()}`)}<b>{activity.Value}</b></span>);
    case ACTIVITY_ACTION.ASSIGNED:
      return (<span>{t(`ticket-details.ticket-conversation.to`)}{<AssignResolver activity={activity} isEndUserPage={isEndUserPage} />}</span>);
    case ACTIVITY_ACTION.REMOVED:
      return (activity.Field === ACTIVITY_FIELD.TICKET_TASKS)
        ? (
          <span>
            <b>{activity.Value}</b>
          </span>
        )
        : _add_remove;
    case ACTIVITY_ACTION.ATTACHED:
      return (<span><b><PropertyResolver activity={activity} isEndUserPage={isEndUserPage} /></b></span>);
    case ACTIVITY_ACTION.MERGED:
      return _relate_unrelate;
    case ACTIVITY_ACTION.RELATED:
      return _relate_unrelate;
    case ACTIVITY_ACTION.UNRELATED:
      return _relate_unrelate;
    case ACTIVITY_ACTION.APPROVED:
    case ACTIVITY_ACTION.DECLINED:
      return _approval_response;
    case ACTIVITY_ACTION.RESET_VOTES_FOR:
      let approvalTitle = "";
      let currentPhase = null;
      try {
        const value = JSON.parse(activity.Value);
        const approval: Approval = value["Approval"];
        currentPhase = approval["TicketLifecyclePhase"]
      } catch {
        approvalTitle = activity.Value;
      }
      return <>
        <span>
          {' '}approval {<b>{approvalTitle}</b>}
        </span>
        {(currentPhase?.TransitionMessage ?? "").length > 0 && (
          <Flex vAlign="center" gap="gap.smaller">
            <AdditionalInfoIcon />{' '}
            <span className={classNames.hint}>{currentPhase.TransitionMessage}</span>
          </Flex>
        )}
      </>;
    case ACTIVITY_ACTION.FILLED:
      const activityValue = JSON.parse(activity.Value);
      return <>
        <span>{<b>{activityValue.FormName}</b>}</span> 
      </>;
    
    case ACTIVITY_FIELD.CUSTOM_PROPERTIES:
      let cpValue = activity.Value;
      const cpPropertyId = activity.Field;
      const cpField = currentState?.customProperties?.find(c => c.PropertyId == cpPropertyId);
      switch(CustomPropertyDataType_LABELS[cpField?.DataType ?? ""]){
        case CustomPropertyDataType_LABELS.DATE:
          cpValue = ConvertDateToLocale(cpValue, DateTime.DATE_FULL);
          break;
        case CustomPropertyDataType_LABELS.TIME:
          cpValue = renderTimeInputInRequest(formatTimeInput(cpValue));
          break;
        case CustomPropertyDataType_LABELS.LIST:
          const itemValue: any[] = cpValue?.toString().split(",") ?? [];
          const item = cpField?.CustomPropertyListItems?.filter(c => itemValue.some(i => i == c.Id.toString())).map(m => m.Value) ?? [];
          cpValue = item.length > 0 ? item.join(", ") : null;
          break;
        default:
          break;
      }
      const displayField = ` ${cpField?.Name ?? cpPropertyId}`;
      const conversationToStr = t(`ticket-details.ticket-conversation.to`);
      if((cpValue?.length ?? 0) == 0) 
        return <span>{displayField}</span>;
      else
        return <>
          <span>
            {`${displayField} ${conversationToStr}`}{<b>{cpValue}</b>}
          </span> 
        </>;
    default:
      return <></>;
  }
};

const ChangeResolver = (props: { activity: ActivityItemProps, isEndUserPage: boolean }) => {

  let { activity, isEndUserPage } = props;
  if (activity.Field === ACTIVITY_FIELD.CARD_ANSWER) {
    return (<span>{i18next.t('ticket-details.ticket-conversation.field.card-answer')}</span>);
  } else if (activity.Field === ACTIVITY_FIELD.SURVEY) {
    return <><span>{i18next.t('ticket-details.ticket-conversation.field.survey')}<AssignResolver activity={activity} isEndUserPage={isEndUserPage} /></span></>;
  } else if (activity.Field === ACTIVITY_FIELD.TICKET_TASK || activity.Field === ACTIVITY_FIELD.TICKET_TASKS) {
    const value = JSON.parse(activity.Value);
    return ticketTaskChange(value, activity, isEndUserPage);
  } else if (activity.Field === ACTIVITY_FIELD.TICKET_APPROVAL) {
    const value = JSON.parse(activity.Value);
    return ticketApprovalChange(value, activity, isEndUserPage);
  } else if (activity.Field === ACTIVITY_FIELD.LIFECYCLE_PHASE) {
    const value: TicketLifecyclePhase = JSON.parse(activity.Value);
    return ticketLifecyclePhaseChange(value);
  } else if (activity.Field === ACTIVITY_FIELD.LIFECYCLE_STATUS) {
    const value: TicketLifecycle = JSON.parse(activity.Value);
    return ticketLifecycleStatusChange(value);
  } else if (activity.Field === ACTIVITY_FIELD.LIFECYCLE) {
    return (<span>{i18next.t('ticket-details.ticket-conversation.field.lifecycle')}<strong>{activity.Value}</strong></span>);
  } else if (isResolutionNoteField(activity)) {
    let txtHeader = i18next.t(`ticket-details.ticket-conversation.field.${activity.Field.hyphenize()}`);
    let iconComponent = replaceTokenWithComponent(txtHeader, "{0}", <LockSolidIcon style={{ marginTop: '-6px' }} className='text-private' />);
    return <>
      <b>{iconComponent}</b><br />
      <span className={`${classNames.resolutionNoteContents} ui-box ei av xc xd xe ui-chat__message__content`}>
        <MarkdownCard activity={activity} />
      </span>
    </>;
  } else if (activity.Field === ACTIVITY_FIELD.TICKET_CUSTOM_FORM){
    const activityValue = JSON.parse(activity.Value);
    return (<span>answers in form <strong>{activityValue.FormName}</strong></span>);
  } else if (activity.Field === ACTIVITY_FIELD.TICKET_CUSTOM_FORM_ANSWER) {
    const activityValue = JSON.parse(activity.Value);
    const newValue = renderCustomFormAnswer(activityValue.NewAnswer);
    const hasOldValue = activityValue.OldAnswer?.trim();

    return (<span>
      <strong>{activityValue.Question}</strong> in{" "}
      <strong>{activityValue.FormName}</strong>{" "}
      {hasOldValue && (
        <>
          from <strong>{renderCustomFormAnswer(activityValue.OldAnswer)}</strong>{" "}
        </>
      )}
      to <strong>{newValue}</strong>
    </span>);
  }
  else {    
    if (activity.Field==ACTIVITY_FIELD.TICKET_RESOLUTION_CATEGORY) {
      const state = appState();
      let resCat = state.resolutionCategory?.find(x => x.Id.toString() == activity.Value);
      activity.Value = resCat?.Value ?? activity.Value;
    }
    return <span>{i18next.t(`ticket-details.ticket-conversation.field.${activity.Field.hyphenize()}`)}<AssignResolver activity={activity} isEndUserPage={isEndUserPage} /></span>;
  }
}

const isResolutionNoteField = (activity: ActivityItemProps) => ([ACTIVITY_FIELD.TICKET_RESOLUTION_NOTE, ACTIVITY_FIELD.TICKET_RESOLUTIONNOTE_INTERNAL].includes(activity.Field));

const resolutionNoteStyle = (activity: ActivityItemProps) => {
  let style = '';
  if (isResolutionNoteField(activity)) {
    style = classNames.resolutionNoteContainer;

    if (activity.Field == ACTIVITY_FIELD.TICKET_RESOLUTIONNOTE_INTERNAL) style += ` ${classNames.internalResolutionNoteContainer}`;
  }

  return style;
}

const PropertyResolver = (props: { activity: ActivityItemProps, isEndUserPage: boolean }): JSX.Element => {
  let { activity, isEndUserPage } = props;

  if (activity.Field === ACTIVITY_FIELD.TAG) {
    let tag = CommonHelper.toObject<TagProps>(activity.Value);
    return <>{tag.Name}</>;
  }

  if (activity.Field === ACTIVITY_FIELD.TICKET_COLLABORATORS || activity.Field === ACTIVITY_FIELD.TASK_COLLABORATORS) {
    let ticketCollaborators = CommonHelper.toObject<AUCProps>(activity.Value);
    return !isEndUserPage ? <UserInfoLink user={{ ...ticketCollaborators.PlatformUser, Name: ticketCollaborators.PlatformUser?.FullName }} /> : <b>{ticketCollaborators.PlatformUser?.FullName}</b>;
  }

  if (activity.Field === ACTIVITY_FIELD.AFFECTED_USERS) {
    let affectedUser = CommonHelper.toObject<AUCProps>(activity.Value);
    return !isEndUserPage ? <UserInfoLink user={{ ...affectedUser.PlatformUser, Name: affectedUser.PlatformUser?.FullName }} /> : <b>{affectedUser.PlatformUser?.FullName}</b>;
  }

  if (activity.Field === ACTIVITY_FIELD.FILE_ATTACHMENT) {
    let attachment = CommonHelper.toObject<AttachmentProps>(activity.Value);
    let isRemoved = activity.Action === ACTIVITY_ACTION.REMOVED ? true : false;

    return (
      <>
        {isRemoved ? (
          <span> {attachment.FileName} </span>
        ) : (
          <Link
            className={`${classNames.userLink} font-bold`}
            target="_blank"
            href={attachment.DownladUrl}
          >
            {attachment.FileName}
          </Link>
        )}
      </>
    )
  }
  return <>{activity.Value}</>;
};


const MarkdownCard = (props: { activity: ActivityItemProps; }) => {
  const { activity } = props;
  return <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
    {activity.Value == "" ? "" : activity.Value}
  </Markdown>

};

const AssignResolver = (props: { activity: ActivityItemProps; isEndUserPage: boolean; }) => {
  const { activity, isEndUserPage } = props;
  const { t } = useTranslation();

  if (activity.Field === ACTIVITY_FIELD.REQUESTER || activity.Field === ACTIVITY_FIELD.ASSIGNEE) {
    let user = userInfo(activity.Value);
    let userInfoShow = !isEndUserPage ? (
      <UserInfoLink user={user} />
    ) : (
      <b>{user?.Name}</b>
    );

    return user.Name === 'Unassigned' ? <b>{t('ticket.ticket-board.ticket-card.unassigned')}</b> : userInfoShow;
  }

  return <b>{fieldFormatting(activity.Field, activity.Value)}</b>;
};

const sepratorOptions = (isHide: boolean = false) => {
  return {
    root: {
      zIndex: 1001,
      ...{
        '::before': {
          backgroundColor: 'var(--mgt-theme-default-foreground2)',
          opacity: isHide ? 0 : 0.1,
          height: 1
        }
      },
      ...{
        '::after': {
          backgroundColor: 'var(--mgt-theme-default-foreground2)',
          opacity: isHide ? 0 : 0.1,
          height: 1
        }
      }
    },
    content: {
      backgroundColor: 'var(--mgt-theme-background2)',
      padding: '5px 5px 5px 0',
      fontWeight: '600'
    }
  };
};

const fieldFormatting = (field: string, value: string) => {
  if (field === 'DueDate' || field === 'ResolutionDate')
    return ConvertDateToLocale(value, DateTime.DATE_FULL);

  return value === 'Unassigned' ? i18next.t('ticket.ticket-board.ticket-card.unassigned') : value;
};

const getActivityIcon = (activity: any) => {

  if (activity.Action == ACTIVITY_ACTION.RESET_VOTES_FOR)
    return <LifecycleIcon height='35' />

  if (activity.Field == ACTIVITY_FIELD.TICKET_TASKS && activity.Action == ACTIVITY_ACTION.CHANGED) {
    const taskData = JSON.parse(activity.Value);
    const taskDataPhase: TicketLifecyclePhase = (taskData[ACTIVITY_TASK_FILED.LIFECYCLE_PHASE]?.length ?? 0) > 0 ? JSON.parse(taskData[ACTIVITY_TASK_FILED.LIFECYCLE_PHASE]) : null;
    if ((taskDataPhase?.LastCompletionDate ?? "").length > 0) {
      return <LifecycleIcon height='35' />
    }
  }

  switch (activity.Field) {
    case ACTIVITY_FIELD.RELATE_TICKET:
      return getRelationIcon(activity.Action);
    case ACTIVITY_FIELD.FILE_ATTACHMENT:
      return getFileAttachmentIcon(activity.Action);
    case ACTIVITY_FIELD.APPROVAL:
      return <ApprovalAddIcon />
    case ACTIVITY_FIELD.TICKET_APPROVAL:
    case ACTIVITY_FIELD.APPROVERS:
      return getApprovalsIcon(activity.Action);
    case ACTIVITY_FIELD.TASK_COLLABORATORS:
    case ACTIVITY_FIELD.TICKET_COLLABORATORS:
      return getTicketCollaboratorsIcon(activity.Action);
    case ACTIVITY_FIELD.AFFECTED_USERS:
      return getTicketAffectedUsersIcon(activity.Action);
    case ACTIVITY_FIELD.TICKET_TASK:
    case ACTIVITY_FIELD.TICKET_TASKS:
      return getTicketTaskIcon(activity.Action);
    case ACTIVITY_FIELD.TIME_ENTRIES:
      return getTimeEntryIcon(activity.Action);
    case ACTIVITY_FIELD.LIFECYCLE_PHASE:
      return <LifecyclePhaseIcon />
    case ACTIVITY_FIELD.LIFECYCLE_STATUS:
      return <LifecycleIcon height='35' />
    default:
      return <FontIcon className={classNames.icon} iconName={iconMapping(activity)} />;
  }
}

const getActivityActionMessage = (activity: ActivityItemProps, t: any) => {
  switch (activity.Field) {
    case ACTIVITY_FIELD.SURVEY:
    case ACTIVITY_FIELD.LIFECYCLE:
      return "";
    case ACTIVITY_FIELD.TICKET_TASKS:
      let modifierInfo = userInfo(activity.ModifierInfo);
      return (modifierInfo.Name == Constants.TicketLifecycle && activity.Action != ACTIVITY_ACTION.CREATED) ? t(`ticket-details.ticket-conversation.actions.task-reset-state`) : `${t(`ticket-details.ticket-conversation.actions.${activity.Action}`)} ${t(`ticket-details.ticket-conversation.actions.task`)}`;
    case ACTIVITY_FIELD.LIFECYCLE_PHASE:
      return t(`ticket-details.ticket-conversation.actions.lifecycle-transitioned-phase`);
    case ACTIVITY_FIELD.LIFECYCLE_STATUS:
      return t(`ticket-details.ticket-conversation.actions.lifecycle-changed-status`);
    default:
      if(activity.Action == ACTIVITY_FIELD.CUSTOM_PROPERTIES){
        return t(`ticket-details.ticket-conversation.actions.${(activity.Value?.length ?? 0) > 0 ? 'changed' : 'cleared-field'}`);
      }else
        return t(`ticket-details.ticket-conversation.actions.${activity.Action}`);
  }
}

export const resolveCommentDate = (date: string) => {
  const normalize = (num: number) => Math.floor(Math.abs(num));

  let d = DateTime.fromJSDate(new Date(date));
  let day = d.toLocaleString({ day: 'numeric' });
  let today = DateTime.now().toLocaleString({ day: 'numeric' });

  if (normalize(d.diffNow().as('days')) < 1 && day === today)
    return d.toLocaleString(DateTime.TIME_SIMPLE);

  return ConvertDateToLocale(date, DateTime.DATETIME_MED);
};

// Export Components

const getTicketAffectedUsersIcon = (activityAction: string) => {
  switch (activityAction) {
    case ACTIVITY_ACTION.ADDED:
      return <AddUserIcon />
    case ACTIVITY_ACTION.REMOVED:
      return <RemoveUserIcon />
    default:
      return <></>;
  }
}
export const skeletonLoader = (commentWidth: string = '300px') => {
  return {
    comment: {
      contentPosition: 'end',
      gutter: (
        <Skeleton animation="wave">
          <Skeleton.Shape round width="25px" height="25px" />
        </Skeleton>
      ),
      message: {
        content: (
          <Stack style={{ width: '100%' }}>
            <Separator
              alignContent="end"
              style={{ marginRight: -2 }}
              styles={{ ...sepratorOptions(true) }}
            >
              <Box
                style={{
                  background: 'var(--mgt-theme-brand-background1)',
                  height: 50,
                  padding: 10,
                  width: commentWidth
                }}
              >
                <Skeleton animation="wave">
                  <Skeleton.Line width="100px" height="10px"></Skeleton.Line>
                </Skeleton>
                <Skeleton animation="wave">
                  <Skeleton.Line height="20px"></Skeleton.Line>
                </Skeleton>
              </Box>
            </Separator>
          </Stack>
        ),
        style: { width: '100%' }
      },
      style: { overflow: 'hidden' },
      key: `activity-id-${Guid.newGuid()}`
    },
    activity: {
      contentPosition: 'end',
      message: {
        content: (
          <>
            {[1, 2, 3].map(k => {
              return (
                <Stack style={{ width: '100%' }} key={k}>
                  <Separator
                    alignContent="end"
                    style={{ marginRight: -2 }}
                    styles={{ ...sepratorOptions() }}
                  >
                    <Flex gap="gap.small" style={{ paddingLeft: 5 }}>
                      <Flex.Item>
                        <Skeleton animation="wave">
                          <Skeleton.Line
                            width="25px"
                            height="18px"
                          ></Skeleton.Line>
                        </Skeleton>
                      </Flex.Item>
                      <Flex.Item>
                        <Skeleton animation="wave">
                          <Skeleton.Line
                            width="240px"
                            height="18px"
                          ></Skeleton.Line>
                        </Skeleton>
                      </Flex.Item>
                    </Flex>
                    <Flex space="between">
                      <div></div>
                      <Flex>
                        <Flex.Item push></Flex.Item>
                        <Flex.Item>
                          <Skeleton animation="wave">
                            <Skeleton.Line
                              width="50px"
                              height="12px"
                            ></Skeleton.Line>
                          </Skeleton>
                        </Flex.Item>
                      </Flex>
                    </Flex>
                  </Separator>
                </Stack>
              );
            })}
          </>
        ),
        style: { width: '100%' }
      },
      style: { overflow: 'hidden' },
      key: `activity-id-${Guid.newGuid()}`
    }
  };
};

export const useActivity = () => {
  const [activityState, setActivities] = useState<any>(defaultActivityState);
  const getTicketActivitiesById = async (
    entityId: number,
    commentOnly: boolean,
    filter: string = '',
    isTrigger = false,
    reloadAll = false
  ) => {

    try {
      let topNumber = 0;
      let skipNumber = 0;
      let skipIncrement = 0;

      if (isTrigger) {
        // Scrolling up: fetch the next page of older activities to prepend.
        topNumber = CONFIG.QUERY_TOP;
        skipNumber = activityState.QuerySkip;
        skipIncrement = activityState.QuerySkip + CONFIG.INCREMENT_SKIP;
      } else if (reloadAll && !activityState.Loading && activityState.QuerySkip > CONFIG.QUERY_TOP) {
        // Refresh after editing/deleting a comment: reload every page that is
        // currently loaded so the list doesn't collapse back to the latest page
        // and scroll the user to the bottom. Keeps them where they were.
        // skipNumber stays 0 (its initial value) to reload from the first page.
        topNumber = activityState.QuerySkip;
        skipIncrement = activityState.QuerySkip;
      } else {
        // Initial load / filter / search: fetch the first page.
        topNumber = CONFIG.QUERY_TOP;
        skipIncrement = CONFIG.INCREMENT_SKIP;
      }

      let apiResult = await api.getTicketActivities(entityId, topNumber, skipNumber, commentOnly, filter);
      let result = apiResult.data.value;

      // Get Users Here
      const ids = result.filter(x => [ACTIVITY_FIELD.TICKET_COLLABORATORS, ACTIVITY_FIELD.TASK_COLLABORATORS, ACTIVITY_FIELD.AFFECTED_USERS].includes(x.Field))
        .map(x => CommonHelper.toObject<AUCProps>(x.Value).PlatformUserId);

      const users = await getCachedUsers(ids);
      result.forEach(x => {
        if ([ACTIVITY_FIELD.TICKET_COLLABORATORS, ACTIVITY_FIELD.TASK_COLLABORATORS, ACTIVITY_FIELD.AFFECTED_USERS].includes(x.Field)) {
          const auc = CommonHelper.toObject<AUCProps>(x.Value);
          auc.PlatformUser = users.find(u => u.Id == auc.PlatformUserId);
          x.Value = JSON.stringify(auc);
        }
      });

      let data = (isTrigger) ? [...result, ...activityState.Activities] : result;

      setActivities({ Activities: data, Loading: false, QuerySkip: skipIncrement });

      return {
        activities: data,
        endOfList: result.length < topNumber
      };

    } catch (error) {
      return defaultActivityState;
    }

  };

  return [activityState, getTicketActivitiesById];
};

const getActivityItem = (activity: ActivityItemProps, position: string, isEndUserPage: boolean) => {
  let displayAsLifecycleAction = false;
  if (activity.Action == ACTIVITY_ACTION.TRANSITIONED || activity.Action == ACTIVITY_ACTION.RESET_VOTES_FOR)
    displayAsLifecycleAction = true;
  else if (activity.Field === ACTIVITY_FIELD.LIFECYCLE && activity.Action === ACTIVITY_ACTION.CHANGED)
    displayAsLifecycleAction = true;
  else if (activity.Field == ACTIVITY_FIELD.TICKET_TASKS) {
    if (activity.Action == ACTIVITY_ACTION.CREATED && !activity.ModifiedById)
      displayAsLifecycleAction = true;
    if (activity.Action == ACTIVITY_ACTION.CHANGED) {
      const taskData = JSON.parse(activity.Value);
      const taskDataPhase: TicketLifecyclePhase = (taskData[ACTIVITY_TASK_FILED.LIFECYCLE_PHASE]?.length ?? 0) > 0 ? JSON.parse(taskData[ACTIVITY_TASK_FILED.LIFECYCLE_PHASE]) : null;
      if ((taskDataPhase?.LastCompletionDate ?? "").length > 0)
        displayAsLifecycleAction = true;
      else if (!taskData.hasOwnProperty(ACTIVITY_TASK_FILED.LIFECYCLE_PHASE) && !activity.ModifiedById)
        displayAsLifecycleAction = true;
    }
  } else if (activity.Action == ACTIVITY_ACTION.ADDED && !activity.ModifiedById && (activity.Field == ACTIVITY_FIELD.APPROVAL || activity.Field == ACTIVITY_FIELD.APPROVERS))
    displayAsLifecycleAction = true;

  if (displayAsLifecycleAction) {
    position = "start";
    let displayLifecycleAsUser = true;

    if (activity.Action == ACTIVITY_ACTION.TRANSITIONED) {
      const lifecycleData = JSON.parse(activity.Value);
      displayLifecycleAsUser = !lifecycleData.IsManuallyTransitionedTo;
    }

    if (displayLifecycleAsUser) {
      activity.ModifierInfo = `{\"Name\":\"Lifecycle\",\"Id\":0,\"Email\":\"\"}`;
    }
  }

  if (activity.Field == "GroupChat") {
    activity.Action = ACTIVITY_ACTION.ADDED;
  }

  return {
    contentPosition: position,
    gutter: position === 'start' ? <div className={classNames.lifecycleIcons}>{getActivityIcon(activity)}</div> : <></>,
    message: {
      content: (
        <Flex className={`items-center justify-end`} style={{ width: '100%' }}>
          <TicketActivity
            position={position}
            activityItem={activity}
            isNoIcon={position === 'start' ? true : false}
            isEndUserPage={isEndUserPage}
          />
        </Flex>
      ),
      style: { width: '90%' }
    },
    style: { overflow: 'hidden' },
    key: `activity-id-${activity.Id}`
  };
}

export const replaceTikitImgToMarkDown = (str, attachments) => {
  const matches = collectTikitImgTagStrings(str);
  let strLength = str.length;
  let resultStr = str;

  matches.forEach(function (match) {
    const itemId = extractTikitImgItemId(match);
    if (!itemId) {
      return;
    }
    let matchIndex = str.indexOf(match);
    let attachment = attachments.find((x) =>
      getAttachmentPublicUrl(x as EditTicketAttachmentsProps).includes(itemId)
    );

    if (attachment) {
      const url = getAttachmentPublicUrl(attachment as EditTicketAttachmentsProps);
      const inlineSrc = attachment.ThumbnailUrl ?? url;
      // Only the src survives into the markdown, so record which attachment it belongs to;
      // the renderer needs the id to ask for a fresh link when this one has expired.
      registerThumbnailSource(inlineSrc, attachment.Id);
      let formatImg = `[![${attachment.FileName} -tikitimg](${inlineSrc})](${url})`;
      // matchIndex is an offset into the ORIGINAL str, but resultStr grows with each
      // replacement, so it has to be shifted by the length difference. Using matchIndex
      // directly silently corrupts any body carrying more than one image.
      let i = (strLength === resultStr.length) ? matchIndex : matchIndex + (resultStr.length - strLength);
      resultStr = resultStr.slice(0, i) + formatImg + resultStr.slice(i + match.length);
    }
  });
  return resultStr
    .replace(/\n/g, '&nbsp; \n')
    .replace(/<tikit-comment-empty.*?><\/tikit-comment-empty>/gi, '');
}

export type ChatActivityItemOptions = {
  teamsConfig: IRequestPageConfig;
  globalTheme: any;
  canEditOwnComments: boolean;
  canDeleteOwnComments: boolean;
  onDeleteSuccess?: () => void;
  onCommentUpdated?: () => void;
};

export const chatActivityItem = (
  activity: ActivityItemProps,
  position: string,
  signedInUser: any,
  isEndUserPage: boolean,
  options: ChatActivityItemOptions
) => {
  const { canEditOwnComments, canDeleteOwnComments, onDeleteSuccess, onCommentUpdated } = options;

  if (activity.Action.trim() === "") {
    return;
  }

  if (activity.Field === ACTIVITY_FIELD.COMMENTS) {
    let comment = JSON.parse(activity.Value || '{}') as Comment;
    const withEmptyBody = comment.Body.replaceAll(new RegExp("<tikit-comment-empty.*?></tikit-comment-empty>", "gi"), '');
    if (withEmptyBody.length == 0 && (comment.FileAttachments?.length ?? 0) == 0)
      return;
    const modifierInfo = userInfo(activity.ModifierInfo);
    let userInfoShow = comment.CreatedById == null ? (<Label className={classNames.automations}>{Constants.Automations}</Label>)
      : !isEndUserPage ? (<UserInfoLink user={modifierInfo} />)
        : (<b>{modifierInfo?.Name}</b>);

    // Northstar pairs a 100px `offset` margin with maxWidth: calc(100% - 100px), and the two
    // cancel so a bubble gets exactly its content width. Overriding marginRight to 8px below
    // leaves the subtraction uncancelled, costing these bubbles 92px and wrapping text that
    // would otherwise fit on one line. #35306
    const chatMessageStyle = (): React.CSSProperties => {
      if ((comment?.FileAttachments?.length ?? 0) === 0 && position !== 'end') {
        return { minWidth: '170px', marginRight: '8px', maxWidth: '100%' }
      } else if ((comment?.FileAttachments?.length ?? 0) > 0 && position !== 'end') {
        return { minWidth: '170px', marginLeft: '16px', marginRight: '8px', paddingLeft: '0px', maxWidth: '100%' }
      } else if ((comment?.FileAttachments?.length ?? 0) > 0 && position === 'end') {
        return { minWidth: '170px', marginLeft: '16px', paddingLeft: '0px' }
      } else if (position !== 'end') {
        return { minWidth: '170px', marginLeft: '16px' }
      }
    }

    return {
      contentPosition: position,
      // signedInUser is not always populated - the end-user request page does not supply one -
      // and with no email and no name the avatar has neither a photo nor initials to fall back
      // on, so it renders as an empty circle. Look the author up by id when that happens.
      gutter: position === 'end' && comment.CreatedById != null && signedInUser?.Email ?
        <UserAvatar {...{ idOrUpn: signedInUser.Email, avatarProps: { name: signedInUser.FullName, size: 'smaller' } }} /> :
        <PlatformUserAvatar id={comment.CreatedById} />,
      message: {
        className: `w-full md:w-11/12 min-w-0 max-w-full flex ${position === 'end' ? 'flex-row-reverse' : ''}`,
        content: (
          <>
          <TicketChatContent 
            comment={comment} 
            ticketId={activity.TicketId}
            isEndUserPage={isEndUserPage}
            canEditOwnComments={canEditOwnComments}
            canDeleteOwnComments={canDeleteOwnComments}
            position={position} 
            chatMessageStyle={chatMessageStyle}
            attachments={renderAttachments(
              comment.Body,
              (comment.FileAttachments || []) as unknown as EditTicketAttachmentsProps[]
            )}
            userInfoShow={userInfoShow}
            onDeleteSuccess={onDeleteSuccess}
            onCommentUpdated={onCommentUpdated}
          />
          </>
        )
      },
      key: `activity-id-${activity.Id}`,
    };
  } else {
    return getActivityItem(activity, position, isEndUserPage);
  }
};

export const TicketActivity: React.FC<{
  activityItem: ActivityItemProps;
  position: string;
  isNoIcon: boolean;
  isEndUserPage?: boolean;
}> = (props: any): JSX.Element => {
  const { t } = useTranslation();
  const currentState = appState();

  let { activityItem, position, isNoIcon, isEndUserPage } = props;

  const ActivityItemMapping: React.FC<{ activity: ActivityItemProps }> = (activityProps: any): JSX.Element => {
    let { activity } = activityProps;
    let modifierInfo = userInfo(activity.ModifierInfo);
    let userInfoShow = !isEndUserPage ? (<UserInfoLink user={modifierInfo} />) : (<b>{modifierInfo?.Name}</b>);

    let modifierElem = modifierInfo.Name === Constants.Automations ? (<Label className={classNames.automations}>{Constants.Automations}</Label>) : (userInfoShow);
    if (modifierInfo.Name === Constants.TicketLifecycle)
      modifierElem = (<Label className={classNames.automations}>{t(`lifecycle.lifecycle`)}</Label>);

    if (activity.Field === ACTIVITY_FIELD.LIFECYCLE && activity.Action === ACTIVITY_ACTION.CHANGED) {
      modifierElem = (<></>);
    }


    if (activity.Action == ACTIVITY_ACTION.CREATED && ["Note", "ResolutionCategory", "DraftKB", "InternalNote"].includes(activity.Field)) activity.Action = ACTIVITY_ACTION.CHANGED;

    let item = {
      key: activity.Id,
      activityDescription: [
        modifierElem,
        <span className={`${classNames.paddingTop10} ${classNames.activityAction}`}>
          {' '}{getActivityActionMessage(activity, t)}{' '}
        </span>
        ,
        <span key={3} className={isResolutionNoteField(activity) ? '' : `${classNames.paddingTop10} ${classNames.activityAction}`}>
          {descriptionMapping(activity, isEndUserPage, t, currentState)}
        </span>
      ],
      timeStamp: (
        <RelativeDate key={4} date={activity.ModifiedDate} isNotShowTime={true} />
      ),
      ...(!isNoIcon && {
        activityIcon: getActivityIcon(activity)
      })
    };

    return (<ActivityItem {...item} key={item.key} className={`${classNames.activityItem} ${resolutionNoteStyle(activity)}`} />);
  };

  return (
    <Stack style={{ width: '100%', zIndex: 1 }}>
      <Separator alignContent={position} styles={{ ...sepratorOptions() }}>
        <span className={`text-left ${classNames.activityDefault}`}>
          <ActivityItemMapping activity={activityItem} />
        </span>
      </Separator>
    </Stack>
  );
};
